import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { setTokens, hasToken } from '../services/authService';
import { apiFetch, getBackendUrl, getUserFacingErrorMessage } from '../services/apiClient';
import './LoginPage.css';

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('password'); // 'password' | 'sso'
  const [ssoInfo, setSsoInfo] = useState(null); // { requires_sso: bool, idp_type: string, init_url: string }
  const [checkingSso, setCheckingSso] = useState(false);
  /** When SSO is recognized in password mode, password is hidden until user chooses this. */
  const [showPasswordWithSso, setShowPasswordWithSso] = useState(false);
  /** false until /api/auth/status confirms allow_registration (avoid open-signup flash on load). */
  const [allowRegistration, setAllowRegistration] = useState(false);
  const emailInputRef = useRef(null);
  const ssoCheckTimeoutRef = useRef(null);

  const ssoRecognizedPasswordMode =
    !isRegistering &&
    loginMode === 'password' &&
    formData.email.includes('@') &&
    !checkingSso &&
    (ssoInfo?.requires_sso || ssoInfo?.sso_available);
  const hidePasswordForSso = ssoRecognizedPasswordMode && !showPasswordWithSso;

  const normalizeSsoInfo = (data) => ({
    ...data,
    // FastAPI returns sso_required/sso_init_url while legacy UI expected requires_sso/init_url.
    requires_sso: Boolean(data.requires_sso ?? data.sso_required),
    sso_available: Boolean(data.sso_available ?? data.available_sso ?? data.sso_required ?? data.requires_sso),
    init_url: data.init_url || data.sso_init_url || null
  });
  
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch('/api/auth/status');
        const d = await r.json();
        if (cancelled) return;
        setAllowRegistration(d.allow_registration === true);
        if (d.allow_registration === false) {
          setIsRegistering(false);
        }
      } catch {
        if (!cancelled) {
          setAllowRegistration(false);
          setIsRegistering(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    checkAuthStatus();
    
    const loginStatus = searchParams.get('login');
    const errorParam = searchParams.get('error');
    
    if (loginStatus === 'success') {
      navigate('/upload');
    } else if (errorParam === 'oauth_failed') {
      setError('OAuth login failed. Please try again.');
    } else if (errorParam === 'registration_disabled') {
      const msg = searchParams.get('message');
      setError(
        msg
          ? decodeURIComponent(msg)
          : 'Sign-up is disabled. Use a company invitation or contact your administrator.'
      );
    } else if (errorParam === 'sso_session_expired' || errorParam === 'sso_config_not_found') {
      setError('SSO session expired. Please try again.');
    } else if (errorParam === 'sso_callback_failed') {
      setError('SSO authentication failed. Please try again.');
    } else if (errorParam === 'sso_userinfo_failed') {
      setError('Failed to retrieve user information from SSO provider. Please try again.');
    }
  }, [navigate, searchParams]);
  
  const checkAuthStatus = async () => {
    // Quick local check first
    if (!hasToken()) return;

    try {
      const response = await apiFetch('/api/auth/status');
      const data = await response.json();
      
      if (data.authenticated) {
        navigate('/upload');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };
  
  const checkSsoRequirement = async (email) => {
    // Clear any pending timeout
    if (ssoCheckTimeoutRef.current) {
      clearTimeout(ssoCheckTimeoutRef.current);
    }
    
    // Reset SSO info if email is invalid
    if (!email || !email.includes('@')) {
      console.log('SSO check: Invalid email, clearing SSO info');
      setSsoInfo(null);
      setCheckingSso(false);
      return;
    }
    
    console.log('SSO check: Checking email:', email);
    
    // Debounce: wait 300ms after user stops typing (reduced from 500ms)
    ssoCheckTimeoutRef.current = setTimeout(async () => {
      console.log('SSO check: Executing check for:', email);
      setCheckingSso(true);
      try {
        const response = await apiFetch(`/api/auth/sso/check?email=${encodeURIComponent(email)}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            getUserFacingErrorMessage(response.status, data, `SSO check failed (${response.status})`)
          );
        }
        const normalizedSsoInfo = normalizeSsoInfo(data);
        console.log('SSO check result:', normalizedSsoInfo);
        setSsoInfo(normalizedSsoInfo);
        
        if (normalizedSsoInfo.requires_sso || normalizedSsoInfo.sso_available) {
          console.log('SSO available! Button should appear.');
        }
      } catch (error) {
        console.error('SSO check failed:', error);
        setSsoInfo(null);
      } finally {
        setCheckingSso(false);
      }
    }, 300); // 300ms debounce (reduced for faster response)
  };

  const handleSsoLogin = () => {
    const backendUrl = getBackendUrl();
    let initUrl;
    
    if (ssoInfo?.init_url || ssoInfo?.sso_init_url) {
      const rawInitUrl = ssoInfo.init_url || ssoInfo.sso_init_url;
      // If init_url is relative, prepend backend URL
      if (rawInitUrl.startsWith('/')) {
        initUrl = `${backendUrl}${rawInitUrl}`;
      } else {
        initUrl = rawInitUrl;
      }
    } else {
      // Fallback: construct URL manually
      initUrl = `${backendUrl}/api/auth/sso/init?email=${encodeURIComponent(formData.email)}`;
    }
    
    console.log('Redirecting to SSO:', initUrl);
    window.location.href = initUrl;
  };

  const switchToSsoMode = () => {
    setLoginMode('sso');
    setShowPasswordWithSso(false);
    setError('');
    setFormData((prev) => ({
      ...prev,
      password: '',
      confirmPassword: ''
    }));
    if (formData.email && formData.email.includes('@')) {
      checkSsoRequirement(formData.email);
    }
    setTimeout(() => emailInputRef.current?.focus(), 0);
  };

  const switchToPasswordMode = () => {
    setLoginMode('password');
    setShowPasswordWithSso(true);
    setError('');
    if (formData.email && formData.email.includes('@')) {
      checkSsoRequirement(formData.email);
    }
  };

  const handleSsoButtonClick = async () => {
    setError('');
    const email = (formData.email || '').trim();
    if (!email) {
      setError('Enter your work email above to use SSO.');
      emailInputRef.current?.focus();
      return;
    }
    if (!email.includes('@')) {
      setError('Enter a valid work email to use SSO.');
      emailInputRef.current?.focus();
      return;
    }
    if (ssoInfo?.requires_sso || ssoInfo?.sso_available) {
      handleSsoLogin();
      return;
    }
    setCheckingSso(true);
    try {
      const response = await apiFetch(`/api/auth/sso/check?email=${encodeURIComponent(email)}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          getUserFacingErrorMessage(response.status, data, 'SSO check failed')
        );
      }
      const normalized = normalizeSsoInfo(data);
      setSsoInfo(normalized);
      if (normalized.requires_sso || normalized.sso_available) {
        const backendUrl = getBackendUrl();
        const rawInitUrl = normalized.init_url || normalized.sso_init_url;
        const initUrl = rawInitUrl
          ? (rawInitUrl.startsWith('/') ? `${backendUrl}${rawInitUrl}` : rawInitUrl)
          : `${backendUrl}/api/auth/sso/init?email=${encodeURIComponent(email)}`;
        window.location.href = initUrl;
        return;
      }
      setError('SSO is not set up for this email. Use password to sign in or contact your admin.');
    } catch (err) {
      console.error('SSO check failed:', err);
      setError(
        err.message || 'Could not check SSO. Use password to sign in or try again.'
      );
    } finally {
      setCheckingSso(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Force update to prevent browser autocomplete from overriding
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }));
    setError('');
    
    // Check SSO requirement when email changes (only for login, not registration)
    if (name === 'email' && !isRegistering) {
      setShowPasswordWithSso(false);
      checkSsoRequirement(value);
    }
    
    // If this is the email field, ensure browser doesn't override it
    if (name === 'email' && emailInputRef.current) {
      // Force the value to stay as user typed it
      setTimeout(() => {
        if (emailInputRef.current && emailInputRef.current.value !== value) {
          emailInputRef.current.value = value;
        }
      }, 0);
    }
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Robust fallback: if password is blank, always do a fresh SSO check at submit time.
    // This avoids missing SSO due to debounce timing or stale UI state.
    if (!isRegistering && !formData.password.trim()) {
      setCheckingSso(true);
      try {
        const response = await apiFetch(`/api/auth/sso/check?email=${encodeURIComponent(formData.email)}`);
        if (response.ok) {
          const data = await response.json();
          const normalizedSsoInfo = normalizeSsoInfo(data);
          setSsoInfo(normalizedSsoInfo);

          if (normalizedSsoInfo.requires_sso || normalizedSsoInfo.sso_available) {
            const backendUrl = getBackendUrl();
            const rawInitUrl = normalizedSsoInfo.init_url || normalizedSsoInfo.sso_init_url;
            const initUrl = rawInitUrl
              ? (rawInitUrl.startsWith('/') ? `${backendUrl}${rawInitUrl}` : rawInitUrl)
              : `${backendUrl}/api/auth/sso/init?email=${encodeURIComponent(formData.email)}`;
            window.location.href = initUrl;
            return;
          }
        }
      } catch (error) {
        console.error('Submit-time SSO check failed:', error);
      } finally {
        setCheckingSso(false);
      }

      setError('Password is required for this account.');
      return;
    }

    setLoading(true);
    
    try {
      const response = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          remember: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store JWT tokens from the response
        if (data.tokens) {
          setTokens(data.tokens);
        }
        // Redirect to upload page after successful login
        window.location.href = '/upload';
      } else if (data.error === 'SSO_REQUIRED') {
        // Redirect to SSO init
        const backendUrl = getBackendUrl();
        window.location.href = data.sso_init_url || `${backendUrl}/api/auth/sso/init?email=${encodeURIComponent(formData.email)}`;
      } else {
        setError(data.detail || data.error || data.message || 'Login failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!allowRegistration) {
      setError('Public registration is disabled. Use a company invitation or contact your administrator.');
      return;
    }

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiFetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          username: formData.username,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store JWT tokens from the response
        if (data.tokens) {
          setTokens(data.tokens);
        }
        // Redirect to upload page after successful registration
        window.location.href = '/upload';
      } else {
        setError(data.detail || data.error || 'Registration failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>TalentVibe</h1>
          <p>{isRegistering ? 'Create your account' : 'Sign in to your account'}</p>
        </div>
        
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        
        <form 
          onSubmit={(e) => {
            if (!isRegistering && loginMode === 'sso') {
              e.preventDefault();
              handleSsoButtonClick();
              return;
            }
            if (isRegistering) handleRegister(e);
            else handleEmailLogin(e);
          }} 
          className="email-login-form"
          autoComplete="off"
          data-lpignore="true"
        >
          {isRegistering && (
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="Choose a username"
                pattern="[a-zA-Z0-9_-]+"
                minLength="3"
                autoComplete="username"
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="login-identifier">
              {isRegistering ? 'Email' : (!isRegistering && loginMode === 'sso' ? 'Work email' : 'Username or email address')}
            </label>
            <input
              ref={emailInputRef}
              type="text"
              id="login-identifier"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                // Allow all key events including Ctrl+A, Delete, Backspace
                // Don't prevent default - let React handle it
              }}
              onInput={(e) => {
                // Force update on input to override browser autocomplete
                const value = e.target.value;
                setFormData(prev => ({ ...prev, email: value }));
                // Check SSO requirement when typing email (only for login, not registration)
                if (!isRegistering) {
                  setShowPasswordWithSso(false);
                  checkSsoRequirement(value);
                }
                // Prevent browser from overriding
                if (emailInputRef.current && emailInputRef.current.value !== value) {
                  emailInputRef.current.value = value;
                }
              }}
              onPaste={(e) => {
                // Handle paste events
                e.preventDefault();
                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                const newValue = pastedText;
                setFormData(prev => ({ ...prev, email: newValue }));
                if (emailInputRef.current) {
                  emailInputRef.current.value = newValue;
                }
                // Check SSO immediately after paste
                if (!isRegistering) {
                  setShowPasswordWithSso(false);
                  checkSsoRequirement(newValue);
                }
              }}
              onBlur={() => {
                // Check SSO when email field loses focus (user finished typing)
                if (!isRegistering && formData.email && formData.email.includes('@')) {
                  // Clear timeout and check immediately
                  if (ssoCheckTimeoutRef.current) {
                    clearTimeout(ssoCheckTimeoutRef.current);
                  }
                  checkSsoRequirement(formData.email);
                }
              }}
              required
              placeholder={isRegistering ? 'Enter your email' : (!isRegistering && loginMode === 'sso' ? 'Enter your work email' : 'Enter your email or username')}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
            />
            {checkingSso && formData.email.includes('@') && (
              <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                Checking SSO requirements...
              </p>
            )}
          </div>

          {/* Password mode: when this email is SSO-enabled, show Continue with SSO above password */}
          {!isRegistering && loginMode === 'password' && formData.email.includes('@') && (
            <div className="form-group sso-inline-password-mode">
              {checkingSso ? (
                <p style={{ fontSize: '12px', color: '#888', marginTop: 0, marginBottom: '8px' }}>
                  Checking SSO for your email...
                </p>
              ) : (ssoInfo?.requires_sso || ssoInfo?.sso_available) ? (
                <>
                  <button
                    type="button"
                    onClick={handleSsoLogin}
                    className="submit-btn sso-btn"
                  >
                    Continue with{' '}
                    {ssoInfo.idp_type === 'azure'
                      ? 'Microsoft'
                      : ssoInfo.idp_type
                        ? ssoInfo.idp_type.charAt(0).toUpperCase() + ssoInfo.idp_type.slice(1)
                        : 'SSO'}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginTop: '8px', marginBottom: 0 }}>
                    {hidePasswordForSso
                      ? ssoInfo.requires_sso
                        ? 'Your organization requires sign-in with SSO.'
                        : 'SSO is available for your email.'
                      : ssoInfo.requires_sso
                        ? 'You can also continue with SSO above.'
                        : 'You can also sign in with SSO above.'}
                  </p>
                  {hidePasswordForSso && (
                    <div style={{ textAlign: 'center', marginTop: '12px' }}>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => {
                          setShowPasswordWithSso(true);
                          setFormData((prev) => ({
                            ...prev,
                            password: '',
                            confirmPassword: ''
                          }));
                        }}
                      >
                        Sign in with password instead
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
          
          {/* SSO mode: email + SSO actions; password fallback always visible */}
          {!isRegistering && loginMode === 'sso' && (
            <div className="form-group sso-mode-actions">
              {checkingSso ? (
                <button type="button" className="submit-btn sso-btn" disabled>
                  Checking...
                </button>
              ) : (ssoInfo?.requires_sso || ssoInfo?.sso_available) ? (
                <>
                  <button
                    type="button"
                    onClick={handleSsoLogin}
                    className="submit-btn sso-btn"
                  >
                    Continue with {ssoInfo.idp_type === 'azure' ? 'Microsoft' : (ssoInfo.idp_type ? ssoInfo.idp_type.charAt(0).toUpperCase() + ssoInfo.idp_type.slice(1) : 'SSO')}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginTop: '8px' }}>
                    {ssoInfo.requires_sso ? 'Your company requires SSO login' : 'SSO is available for your company'}
                  </p>
                </>
              ) : formData.email && formData.email.includes('@') && ssoInfo && !ssoInfo.requires_sso && !ssoInfo.sso_available ? (
                <p className="sso-not-set-up-message">
                  SSO is not set up for this email. Use password to sign in or contact your admin.
                </p>
              ) : null}
              <div className="sso-password-fallback" style={{ textAlign: 'center', marginTop: '12px' }}>
                <button type="button" className="link-btn" onClick={switchToPasswordMode}>
                  Sign in with password instead
                </button>
              </div>
            </div>
          )}
          
          {/* Password mode: hide password while SSO is the primary path until user opts in */}
          {!isRegistering && loginMode === 'password' && !hidePasswordForSso && (
            <div className="form-group">
              <div className="password-label">
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="forgot-password">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!isRegistering}
                placeholder="Enter your password"
                minLength="8"
                autoComplete="current-password"
              />
            </div>
          )}
          
          {isRegistering && (
            <>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                  minLength="8"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm your password"
                  minLength="8"
                  autoComplete="new-password"
                />
              </div>
            </>
          )}
          
          {/* Submit: only when password path is visible (or register) */}
          {(isRegistering || (loginMode === 'password' && !hidePasswordForSso)) && (
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading
                ? (isRegistering ? 'Creating account...' : 'Signing in...')
                : (isRegistering
                  ? 'Create account'
                  : ((ssoInfo?.sso_available || ssoInfo?.requires_sso) ? 'Sign in with password' : 'Sign in'))}
            </button>
          )}
        </form>
        
        {!isRegistering && loginMode === 'password' && !ssoRecognizedPasswordMode && (
          <>
            <div className="divider">
              <span>or</span>
            </div>
            <div className="oauth-buttons">
              <button
                type="button"
                onClick={switchToSsoMode}
                className="oauth-btn sso-btn"
              >
                Sign in with SSO
              </button>
            </div>
          </>
        )}
        
        <div className="form-footer">
          {isRegistering ? (
            <p>
              Already have an account?{' '}
              <button 
                type="button" 
                className="link-btn"
                onClick={() => {
                  setIsRegistering(false);
                  setLoginMode('password');
                  setShowPasswordWithSso(false);
                  setFormData({
                    email: formData.email, // Keep email when switching back
                    username: '',
                    password: '',
                    confirmPassword: ''
                  });
                  setError('');
                  setSsoInfo(null);
                  if (formData.email) {
                    checkSsoRequirement(formData.email);
                  }
                }}
              >
                Sign in
              </button>
            </p>
          ) : allowRegistration ? (
            <p>
              Don't have an account?{' '}
              <button 
                type="button" 
                className="link-btn"
                onClick={() => {
                  setIsRegistering(true);
                  setLoginMode('password');
                  setShowPasswordWithSso(false);
                  setFormData({
                    email: formData.email,
                    username: '',
                    password: '',
                    confirmPassword: ''
                  });
                  setError('');
                  setSsoInfo(null);
                }}
              >
                Create an account
              </button>
            </p>
          ) : (
            <p style={{ color: '#888', fontSize: '0.9rem' }}>
              Self-service signup is disabled. Ask your TalentVibe operator or company admin for an invitation.
            </p>
          )}
        </div>
        
        <div className="login-footer">
          <p>By continuing, you agree to TalentVibe's Terms of Service</p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
